import classNames from 'classnames'
import React from 'react'

import { Button, ButtonSpanProps, type ButtonVariant } from '../../button/button.js'
import { GridColumn } from '../../grid/grid-column.js'
import { Grid } from '../../grid/grid.js'
import { CheckSmallIcon } from '../../icon/icon.js'

export type ButtonGroupValue = string | null

export type ButtonGroupInputAttributes = Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'checked' | 'defaultChecked' | 'onChange' | 'disabled' | 'value'
>

export interface ButtonGroupOption extends Omit<ButtonSpanProps, 'element'> {
    id: string
    input?: ButtonGroupInputAttributes
    value?: string
    checked?: boolean
    displayIcon?: boolean
    onChange?: (checked: boolean) => void
    element?: 'span' | 'button'
}

export interface ButtonGroupProps {
    id: string
    name: string
    options: ButtonGroupOption[]
    value?: ButtonGroupValue
    onChange?: (value: ButtonGroupValue) => void
    className?: string
}

export const ButtonGroup = (props: ButtonGroupProps): JSX.Element => {
    const className: string = classNames('button-group', props.className)

    const handleChange = (val: string, checked: boolean, onChange?: (checked: boolean) => void): void => {
        if (props.onChange) {
            props.onChange(val)
        }

        if (onChange) {
            onChange(checked)
        }
    }

    const isChecked = (val: string): boolean => {
        const currentValue = props.value

        return val === currentValue
    }

    const getOptionInput = (option: ButtonGroupOption): React.InputHTMLAttributes<HTMLInputElement> => {
        return {
            ...option.input,
            name: props.name,
        }
    }

    return (
        <fieldset className={className} id={props.id}>
            <Grid gutter="mobile">
                {props.options.map((option: ButtonGroupOption, index: number) => {
                    const val = option.value || 'on'
                    const onChange = (checked: boolean) => handleChange(val, checked, option.onChange)

                    return (
                        <GridColumn key={index}>
                            <ButtonGroupOption
                                {...option}
                                onChange={onChange}
                                checked={isChecked(val)}
                                input={getOptionInput(option)}
                            />
                        </GridColumn>
                    )
                })}
            </Grid>
        </fieldset>
    )
}

export const ButtonGroupOption = (props: ButtonGroupOption) => {
    const inputRef: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>()
    const { checked, id, input, isDisabled, value } = props

    const className: string = classNames('button-group__option', props.className)

    const onChange = (): void => {
        if (inputRef.current) {
            if (isDisabled || checked) {
                return
            }

            if (props.onChange) {
                props.onChange(true)
            }
        }
    }

    const getVariant = (): ButtonVariant => {
        if (props.variant) {
            return props.variant
        } else {
            return checked ? 'primary' : 'secondary'
        }
    }

    return (
        <div className={className}>
            <input
                {...input}
                value={value}
                type="radio"
                ref={inputRef}
                onChange={onChange}
                checked={checked}
                defaultChecked={undefined}
                id={id}
                className="button-group__input"
            />
            <label htmlFor={id} className="button-group__label">
                <Button
                    {...props}
                    className="button-group__button"
                    element={props.element || 'span'}
                    iconRight={props.displayIcon && checked ? CheckSmallIcon : undefined}
                    isBlock={true}
                    variant={getVariant()}
                />
            </label>
        </div>
    )
}
